/**
 * Messages Index
 *
 * EN core is loaded synchronously (always needed — landing page, nav, auth).
 * EN non-core namespaces are loaded lazily via loadEnNamespaces(group) and
 * merged into messages.en at runtime as pages need them.
 *
 * ES and FR are lazy-loaded in full via loadLocale() — unchanged from before.
 *
 * Subscriber pattern:
 *   When a new EN namespace group is merged, all subscribeToEN listeners are
 *   called so that useTranslation hooks can trigger a re-render to pick up
 *   the freshly available keys.
 */

import coreEn, { loadEnGroup, EnGroup } from './en';

// ─────────────────────────────────────────────────────────────────────────────
// Runtime message store
//
// messages.en starts as the core subset. Non-core namespaces are added via
// Object.assign as groups load. TypeScript sees this as Record<string, unknown>
// because the shape grows dynamically; the t() function already handles missing
// keys gracefully (returns the key string), so no runtime breakage occurs.
// ─────────────────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Messages = Record<string, any>;

export const messages: Record<string, Messages> = {
  en: { ...coreEn },
};

// ─────────────────────────────────────────────────────────────────────────────
// Pub-sub for EN namespace loads
//
// useTranslation subscribes here so it can re-render when new EN keys arrive.
// ─────────────────────────────────────────────────────────────────────────────

const enListeners = new Set<() => void>();

/**
 * Subscribe to EN namespace load events.
 * Returns an unsubscribe function — call it in useEffect cleanup.
 */
export function subscribeToEN(listener: () => void): () => void {
  enListeners.add(listener);
  return () => enListeners.delete(listener);
}

function notifyENListeners(): void {
  enListeners.forEach(fn => fn());
}

// ─────────────────────────────────────────────────────────────────────────────
// EN namespace group loader
//
// Tracks which groups have already been loaded so repeated calls are no-ops.
// Called from App.tsx alongside React.lazy() page imports so namespace chunks
// download in parallel with their page chunks.
// ─────────────────────────────────────────────────────────────────────────────

const loadedEnGroups = new Set<EnGroup>();

/**
 * Lazy-load a group of EN namespaces and merge them into messages.en.
 * Safe to call multiple times — subsequent calls for the same group resolve
 * instantly without a network request.
 *
 * @example
 *   // In App.tsx, co-loaded with the page chunk:
 *   const DashboardPage = lazy(() =>
 *     Promise.all([import('./DashboardPage'), loadEnNamespaces('app')])
 *       .then(([mod]) => mod)
 *   );
 */
export async function loadEnNamespaces(group: EnGroup): Promise<void> {
  if (loadedEnGroups.has(group)) return;
  loadedEnGroups.add(group); // mark before await to prevent duplicate loads

  const namespaces = await loadEnGroup(group);
  Object.assign(messages.en, namespaces);
  notifyENListeners();
}

// ─────────────────────────────────────────────────────────────────────────────
// ES / FR loader — unchanged
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Load a non-English locale bundle on demand.
 * Returns the messages object and caches it into messages[lang] so
 * subsequent calls are instant.
 */
export async function loadLocale(lang: 'es' | 'fr'): Promise<Messages> {
  if (messages[lang]) return messages[lang];

  const mod = lang === 'es'
    ? await import(/* webpackChunkName: "i18n-es" */ './es')
    : await import(/* webpackChunkName: "i18n-fr" */ './fr');

  messages[lang] = mod.default as Messages;
  return messages[lang];
}

export { coreEn as en };
export default messages;